import React, { useState, useEffect, useRef } from 'react';
import { customerApi } from '../../../../api/customerApi';
import './CustomerSelect.css';

const CustomerSelect = ({ 
  value, 
  onChange, 
  placeholder = "Kunde auswählen...",
  disabled = false,
  className = ''
}) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const data = await customerApi.getAll();
        setCustomers(data.filter(c => c.status !== 'deleted'));
      } catch (err) {
        console.error('Failed to fetch customers', err);
      } finally {
        setLoading(false);
      }
    };
    if (!disabled) {
      fetchCustomers();
    }
  }, [disabled]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCustomers = customers.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return c.name?.toLowerCase().includes(term) || c.company?.toLowerCase().includes(term);
  });

  const selectedCustomer = customers.find(c => c.id === value);

  return (
    <div className={`customer-select-wrapper ${className}`} ref={wrapperRef}>
      <div 
        className={`customer-select-header ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="customer-select-value">
          <svg className="customer-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
          {selectedCustomer ? (
            <span className="selected-text">
              {selectedCustomer.name} {selectedCustomer.company ? `(${selectedCustomer.company})` : ''}
            </span>
          ) : (
            <span className="placeholder-text">{placeholder}</span>
          )}
        </div>
        
        <div className="customer-select-actions">
          {value && !disabled && (
            <div 
              className="customer-select-clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                setIsOpen(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </div>
          )}
          <svg className="customer-select-caret" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
      </div>

      {isOpen && (
        <div className="customer-select-dropdown">
          <div className="customer-select-search">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input 
              type="text"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="customer-select-list">
            {loading ? (
              <div className="customer-select-msg">Lädt...</div>
            ) : filteredCustomers.length > 0 ? (
              filteredCustomers.map(c => (
                <div 
                  key={c.id}
                  className={`customer-select-item ${value === c.id ? 'active' : ''}`}
                  onClick={() => {
                    onChange(c.id);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  <div className="customer-item-name">{c.name}</div>
                  {c.company && (
                    <div className="customer-item-company">{c.company}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="customer-select-msg">Keine Kunden gefunden</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerSelect;
